import { useEffect, useRef, useState } from 'react';

// Detección de build viejo ----------------------------------------------------
//
// `src/lib/lazy-with-reload.ts` recupera la pestaña cuando el deploy se llevó un
// chunk, pero solo actúa si alguien navega. Una pestaña que queda abierta sin
// navegar nunca pide un chunk nuevo y se queda con el build viejo indefinidamente.
//
// Acá comparamos el sello compilado en este bundle contra el que sirve el
// servidor en `/version.json` (ambos los escribe `vite-plugins/version-stamp.ts`).
// El hook solo informa: no recarga nada — quien decide es la persona, que puede
// estar a mitad de un formulario.

/** Sello del build servido ahora mismo. `try_files` de nginx lo sirve directo. */
export const VERSION_MANIFEST_URL = '/version.json';

/** Cada cuánto se consulta el sello. Un deploy no es urgente: 5 minutos sobran. */
export const VERSION_POLL_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Ventana mínima entre dos consultas. Sin esto, alguien saltando entre pestañas
 * dispararía un fetch por salto (`visibilitychange` + `focus` llegan juntos).
 */
export const VERSION_CHECK_THROTTLE_MS = 30_000;

export interface AppVersionState {
  /** True cuando el servidor sirve un build distinto al de esta pestaña. */
  isOutdated: boolean;
  /** Sello del build en ejecución. Vacío en dev o en un build sin plugin. */
  buildId: string;
}

/** Sello compilado por el plugin. Se lee en caliente para que los tests lo stubeen. */
function getRunningBuildId(): string {
  const value = import.meta.env.VITE_APP_BUILD_ID;
  return typeof value === 'string' ? value : '';
}

/** Extrae el `buildId` del manifiesto. Cualquier forma inesperada vale null. */
function readBuildId(payload: unknown): string | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const value = (payload as Record<string, unknown>).buildId;
  return typeof value === 'string' && value.length > 0 ? value : null;
}

export function useAppVersion(): AppVersionState {
  const buildId = getRunningBuildId();
  // En dev no hay `version.json` que consultar; sin sello compilado tampoco hay
  // contra qué comparar (build viejo, o servido desde otro pipeline).
  const isEnabled = !import.meta.env.DEV && buildId.length > 0;

  const [isOutdated, setIsOutdated] = useState(false);
  const lastCheckedAtRef = useRef(0);

  useEffect(() => {
    // Una vez desactualizada la pestaña ya no hay nada más que averiguar: el
    // efecto se limpia y deja de haber intervalo ni listeners.
    if (!isEnabled || isOutdated) return undefined;

    let isCancelled = false;

    async function check(): Promise<void> {
      if (isCancelled) return;
      // Pestaña escondida o navegador sin red: no se consulta nada.
      if (document.visibilityState === 'hidden') return;
      if (navigator.onLine === false) return;

      const now = Date.now();
      if (now - lastCheckedAtRef.current < VERSION_CHECK_THROTTLE_MS) return;
      lastCheckedAtRef.current = now;

      try {
        const response = await fetch(VERSION_MANIFEST_URL, { cache: 'no-store' });
        if (!response.ok) return;
        const servedBuildId = readBuildId(await response.json());
        if (isCancelled || servedBuildId === null) return;
        if (servedBuildId !== buildId) setIsOutdated(true);
      } catch {
        // Red caída, proxy corporativo, o el fallback SPA devolviendo el
        // index.html en lugar del json: se ignora en silencio. Avisar de una
        // versión nueva por un hipo de red sería peor que no avisar. Tampoco
        // va a Sentry: es ruido esperable, no un defecto.
      }
    }

    function onWake(): void {
      void check();
    }

    void check();
    const intervalId = window.setInterval(onWake, VERSION_POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);

    return () => {
      isCancelled = true;
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
    };
  }, [isEnabled, isOutdated, buildId]);

  return { isOutdated, buildId };
}
